import DateTimePickerWrapper from '@components/system/CustomDateTimePicker/DateTimePickerWrapper';
import TimePickerWrapper from '@components/system/CustomDateTimePicker/TimePickerWrapper';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  // Input,
  createStyles,
  makeStyles
} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import {
  DEFAULT_BACKGROUND_COLOR,
  FONT_PRIMARY_COLOR,
  FONT_SECONDARY_COLOR
} from 'src/style/variable';
import {Trans, useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {VaccineConsentFormState} from '../../ScheduleAppointment/ScheduleAppointment';
import AddSignatureModal from '../AddSignatureModal/AddSignatureModal';
import {VACCINE_INFO_KEY_MAPPER} from '@shared/constants/Mapping';
import {Vaccine} from '@shared/services/service-mappers/AppointmentSchedulingMapper';
import {getVaccineInformationFile} from '@shared/services/VaccinesService';

const TITLE_FONT_SIZE = '2rem';
const FONT_SIZE = '1.25rem';
const INPUT_FONT_SIZE = '1rem';
const consentFormStyles = makeStyles(() =>
  createStyles({
    consentFormWrapper: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      // maxHeight: '450px',
      fontSize: FONT_SIZE,
      color: FONT_SECONDARY_COLOR,
      padding: '1rem',
      '& p': {
        maxWidth: '55ch'
      },
      overflow: 'auto'
    },
    link: {
      fontSize: FONT_SIZE
    },
    formTitle: {
      color: FONT_PRIMARY_COLOR,
      marginBottom: '2rem',
      fontSize: TITLE_FONT_SIZE
    },
    consentCheckBoxWrapper: {
      '& .MuiFormControlLabel-label': {
        fontSize: FONT_SIZE,
        color: FONT_SECONDARY_COLOR,
        maxWidth: '55ch'
      },
      '& .MuiCheckbox-root': {
        paddingBottom: 0,
        paddingTop: '0.1em'
      },
      alignItems: 'flex-start',
      gap: '1rem'
    },
    vaccineRadioGroupWrapper: {
      '& .MuiFormControlLabel-root': {
        gap: '1rem',
        '& .MuiFormControlLabel-label': {
          fontSize: FONT_SIZE,
          color: FONT_SECONDARY_COLOR,
          maxWidth: '55ch'
        }
      }
    },
    formLabel: {
      marginBlock: '0.5rem',
      fontSize: INPUT_FONT_SIZE
    },
    formControl: {
      paddingBottom: 0,
      marginBottom: 0
    },
    consentFormHeader: {
      paddingBottom: '3rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      borderBottom: `1px solid`,
      marginBottom: '1.5rem'
    },
    highlightedUnderlinedText: {
      fontWeight: 'bold',
      textDecoration: 'underline'
    },
    highlightedText: {
      fontWeight: 'bold'
    },
    consentFormBody: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      paddingBottom: '1rem'
    },
    consentFormCheckboxWrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem'
    },
    consentFormList: {
      listStylePosition: 'inside',
      paddingLeft: '1rem'
    },
    signPadActionButtonWrapper: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: '0.5rem'
    },
    signCanvas: {
      width: '100%',
      height: '15rem',
      backgroundColor: DEFAULT_BACKGROUND_COLOR
    },
    signatureWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start'
    },
    signatureImage: {
      objectFit: 'contain',
      maxHeight: '8rem'
    },
    signatureDialogTitle: {
      paddingBottom: '0.5rem'
    },
    signatureDialogActionButton: {
      '& .MuiButton-label': {
        // fontSize: '1rem'
      }
    },
    signatureDialogActionWrapper: {
      flexWrap: 'wrap-reverse',
      justifyContent: 'center',
      gap: '0.5rem'
    },
    vaccineTitle: {
      display: 'block',
      marginBottom: '0.5rem'
    }
  })
);

export interface IShingrixConsentFormState {
  digitalSignatureURL: string;
  consent: boolean;
  selectedShingrixVaccine: string | undefined;
  consentFormDate: Date;
}

const ShingrixConsentForm = React.forwardRef<
  HTMLDivElement,
  IShingrixConsentFormState & {
    updateConsentFormState: React.Dispatch<
      React.SetStateAction<VaccineConsentFormState>
    >;
    selectedVaccine: Vaccine;
  }
>(
  (props, ref): JSX.Element => {
    const {
      digitalSignatureURL,
      updateConsentFormState,
      consent,
      selectedShingrixVaccine,
      consentFormDate,
      selectedVaccine
    } = props;
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();

    const [signatureState, setSignatureState] = useState({
      isModalOpen: false
    });
    const openSignatureModal = () => {
      setSignatureState(p => ({...p, isModalOpen: true}));
    };

    const closeSignatureModal = () => {
      setSignatureState(p => ({...p, isModalOpen: false}));
    };

    const createSignHandler = (signURL: string) => {
      updateConsentFormState(p => ({
        ...p,
        [VACCINE_INFO_KEY_MAPPER.SHINGRIX]: {
          ...p[VACCINE_INFO_KEY_MAPPER.SHINGRIX],
          digitalSignatureURL: signURL
        }
      }));
      closeSignatureModal();
    };

    const handleDownload = async (): Promise<void> => {
      try {
        getVaccineInformationFile({
          vaccine_type: selectedVaccine.vaccine_info_key
        }).then((res: any) => {
          const url = window.URL.createObjectURL(new Blob([res]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'shingrix_vaccine.pdf');
          document.body.appendChild(link);
          link.click();
        });
      } catch (error: any) {
        enqueueSnackbar(
          (error && error.message) || t('CLINICS.CLINIC_GROUP_CLINICS_ERROR'),
          SnackBarConfig('e')
        );
      }
    };

    const classes = consentFormStyles();

    useEffect(() => {
      updateConsentFormState(p => ({
        ...p,
        [VACCINE_INFO_KEY_MAPPER.SHINGRIX]: {
          ...p[VACCINE_INFO_KEY_MAPPER.SHINGRIX],
          consentFormDate: new Date()
        }
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div ref={ref} className={classes.consentFormWrapper}>
        <div>
          <h1 className={classes.formTitle}>
            {t('APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.TITLE')}
          </h1>
          <div className={classes.consentFormHeader}>
            <p>
              {t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.CDC_INFO'
              )}
            </p>
            <p>
              <span
                className={`${classes.highlightedUnderlinedText} ${classes.vaccineTitle}`}>
                {t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.SINGRIX_VACCINE_TITLE'
                )}
              </span>
              {t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.SINGRIX_VACCINE_INFO'
              )}
            </p>
          </div>
          <div className={classes.consentFormBody}>
            <span
              className={['link-style', classes.link].join(' ')}
              onClick={handleDownload}>
              {t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.CLICK_HERE_TO_DOWNLOAD_VACCINE_INFORMATION_SHEET'
              )}
            </span>
            <p>
              {t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.VACCINE_INFORMATION_SHEET_INFO'
              )}
            </p>
            <ul className={classes.consentFormList}>
              <li>
                {t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.VACCINE_INFORMATION_SHEET_ITEMS.INFORMATION_ITEM_1'
                )}
              </li>
              <li>
                {t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.VACCINE_INFORMATION_SHEET_ITEMS.INFORMATION_ITEM_2'
                )}
              </li>
              <li>
                {t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.VACCINE_INFORMATION_SHEET_ITEMS.INFORMATION_ITEM_3'
                )}
              </li>
              <li>
                {t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.VACCINE_INFORMATION_SHEET_ITEMS.INFORMATION_ITEM_4'
                )}
              </li>
            </ul>
            <p>
              {t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.AUTHORIZE_RELEASE_OF_MEDICAL_INFO'
              )}
            </p>
          </div>
        </div>

        <div className={classes.consentFormCheckboxWrapper}>
          <FormControlLabel
            className={classes.consentCheckBoxWrapper}
            control={
              <Checkbox
                checked={consent}
                onChange={e => {
                  e.persist();
                  updateConsentFormState(p => ({
                    ...p,
                    [VACCINE_INFO_KEY_MAPPER.SHINGRIX]: {
                      ...p[VACCINE_INFO_KEY_MAPPER.SHINGRIX],
                      consent: e.target.checked
                    }
                  }));
                }}
              />
            }
            label={
              <p>
                <Trans
                  i18nKey={
                    'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.UNDERSTAND_BENEFITS_AND_RISKS'
                  }
                  components={[
                    <span className={classes.highlightedText} key="X" />
                  ]}
                />
              </p>
            }
          />

          <p>
            {t(
              'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.PLEASE_CHECK_THE_VACCINE'
            )}
          </p>

          <RadioGroup
            name=""
            value={selectedShingrixVaccine}
            className={classes.vaccineRadioGroupWrapper}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              event.persist();
              updateConsentFormState(p => ({
                ...p,
                [VACCINE_INFO_KEY_MAPPER.SHINGRIX]: {
                  ...p[VACCINE_INFO_KEY_MAPPER.SHINGRIX],
                  selectedShingrixVaccine: event.target.value
                }
              }));
            }}>
            <FormControlLabel
              value="shingrix"
              control={<Radio />}
              label={
                <p className={classes.highlightedText}>
                  {t(
                    'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.SHINGRIX_VACCINE_LABEL'
                  )}
                </p>
              }
            />
            <FormControlLabel
              value="no"
              control={<Radio />}
              label={
                <p>
                  <Trans
                    i18nKey={
                      'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.NO_VACCINE'
                    }
                    components={[
                      <span className={classes.highlightedText} key="X" />
                    ]}
                  />
                </p>
              }
            />
          </RadioGroup>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              {digitalSignatureURL ? (
                <div className={classes.signatureWrapper}>
                  <img
                    src={digitalSignatureURL}
                    className={classes.signatureImage}
                  />
                  <Button
                    onClick={() =>
                      updateConsentFormState(p => ({
                        ...p,
                        [VACCINE_INFO_KEY_MAPPER.SHINGRIX]: {
                          ...p[VACCINE_INFO_KEY_MAPPER.SHINGRIX],
                          digitalSignatureURL: ''
                        }
                      }))
                    }>
                    {t('APPOINTMENTS_SCHEDULAR_PORTAL.REMOVE')}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={openSignatureModal}>
                  {t('APPOINTMENTS_SCHEDULAR_PORTAL.ADD_SIGNATURE')}
                </Button>
              )}
            </Grid>

            <Grid item xs={6} md={3}>
              <DateTimePickerWrapper
                inputVariant="standard"
                disabled
                value={consentFormDate}
                onChange={() => {
                  /**/
                }}
              />
              <p className={classes.formLabel}>
                {t('APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.DATE')}
              </p>
            </Grid>
            <Grid item xs={6} md={3}>
              <TimePickerWrapper
                disabled
                inputVariant="standard"
                value={consentFormDate}
                onChange={() => {
                  /**/
                }}
              />
              <p className={classes.formLabel}>
                {t('APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.TIME')}
              </p>
            </Grid>
          </Grid>
        </div>
        {signatureState.isModalOpen && (
          <AddSignatureModal
            isOpen={signatureState.isModalOpen}
            onClose={closeSignatureModal}
            onCreateSign={createSignHandler}
          />
        )}
      </div>
    );
  }
);

export default ShingrixConsentForm;
